/**
 * JsonViewer - Atom component for displaying JSON data with syntax highlighting
 */
import React, { useState } from 'react';

interface JsonViewerProps {
  data: any;
  title?: string;
  maxHeight?: string;
  collapsed?: boolean;
  showToggle?: boolean;
  className?: string;
}

const JsonViewer: React.FC<JsonViewerProps> = ({
  data,
  title = 'JSON Data',
  maxHeight = '300px',
  collapsed = false,
  showToggle = true,
  className = ''
}) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  
  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
    return (
      <div className={`text-gray-500 italic text-sm ${className}`}>
        No additional data
      </div>
    );
  }
  
  const renderJsonValue = (value: any, indent: number = 0): JSX.Element => {
    const indentClass = `pl-${indent * 4}`;
    
    if (value === null) {
      return <span className="text-gray-400">null</span>;
    }
    
    if (typeof value === 'string') {
      return <span className="text-green-600 dark:text-green-400">"{value}"</span>;
    }
    
    if (typeof value === 'number') {
      return <span className="text-blue-600 dark:text-blue-400">{value}</span>;
    }
    
    if (typeof value === 'boolean') {
      return <span className="text-purple-600 dark:text-purple-400">{value.toString()}</span>;
    }
    
    if (Array.isArray(value)) {
      return (
        <div className={indentClass}>
          <span className="text-gray-600 dark:text-gray-400">[</span>
          {value.map((item, index) => (
            <div key={index} className="ml-4">
              {renderJsonValue(item, indent + 1)}
              {index < value.length - 1 && <span className="text-gray-600 dark:text-gray-400">,</span>}
            </div>
          ))}
          <span className="text-gray-600 dark:text-gray-400">]</span>
        </div>
      );
    }
    
    if (typeof value === 'object') {
      return (
        <div className={indentClass}>
          <span className="text-gray-600 dark:text-gray-400">{'{'}</span>
          {Object.entries(value).map(([key, val], index, arr) => (
            <div key={key} className="ml-4">
              <span className="text-orange-600 dark:text-orange-400">"{key}"</span>
              <span className="text-gray-600 dark:text-gray-400">: </span>
              {renderJsonValue(val, indent + 1)}
              {index < arr.length - 1 && <span className="text-gray-600 dark:text-gray-400">,</span>}
            </div>
          ))}
          <span className="text-gray-600 dark:text-gray-400">{'}'}</span>
        </div>
      );
    }
    
    return <span>{String(value)}</span>;
  };

  return (
    <div className={`border rounded-lg dark:border-gray-700 ${className}`}>
      <div className="flex items-center justify-between p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <h4 className="font-medium text-gray-900 dark:text-gray-100">{title}</h4>
        {showToggle && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm font-medium"
            aria-label={isCollapsed ? 'Expand JSON' : 'Collapse JSON'}
          >
            {isCollapsed ? '▼ Show' : '▲ Hide'}
          </button>
        )}
      </div>
      
      {!isCollapsed && (
        <div 
          className="p-3 overflow-auto bg-white dark:bg-gray-900 font-mono text-sm"
          style={{ maxHeight }}
        >
          <pre className="whitespace-pre-wrap">
            {renderJsonValue(data)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default JsonViewer; 